import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "@/lib/brand";
import { BookOpen, ArrowRight } from "@phosphor-icons/react";

/**
 * Compact "Your Forecast Story" snapshot for the Dashboard.
 * Iter39 — trimmed to a pure teaser:
 *   • FIRST paragraph only, trailed with "…"
 *   • Deep-link "Read your full Forecast Story →" to /forecast#forecast-story
 *   • Removed the standalone "This week's focus" quote block (it echoed the
 *     user's own challenge with no interpretation and got repetitive quickly —
 *     the full "This Week's Focus" section on the Forecast page carries the
 *     interpreted version).
 */
export default function ForecastStorySnapshot() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const storyRes = await axios.get(`${API}/ai/forecast-story`);
        const paras = storyRes.data?.paragraphs || [];
        if (paras.length > 0) {
          const first = (paras[0] || "").replace(/^\s+/, "");
          const m = first.match(/^\*\*(.+?)\*\*\s*\n?([\s\S]*)$/);
          const body = (m ? m[2].trim() : first);
          // Iter39 — always trail off with "…" so users know there's more on
          // the Forecast page. Strip any trailing sentence-ender first so we
          // don't end with ".…" or "!…".
          setSnapshot(body.replace(/[.!?]?\s*$/, "…"));
        }
      } catch {
        /* silent — dashboard should still render */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (!snapshot) return null;

  return (
    <div
      className="card border-l-[6px] border-l-[#FFBD4A]"
      data-testid="forecast-story-snapshot"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="section-eyebrow flex items-center gap-2">
          <BookOpen size={14} weight="duotone" /> Your forecast story
        </div>
      </div>
      <p className="mt-3 leading-relaxed text-black/85" data-testid="forecast-story-snapshot-para">
        {snapshot}
      </p>
      <Link
        to="/forecast#forecast-story"
        className="mt-4 btn-primary inline-flex"
        data-testid="forecast-story-snapshot-link"
      >
        Read your full Forecast Story <ArrowRight size={13} weight="bold" />
      </Link>
    </div>
  );
}
